/**
 * Shapes for the On-Page SEO Checker (/tools/on-page-seo-checker).
 *
 * Everything persisted on `tools.tool_runs` for this tool is declared here:
 * `input`, `facts`, `findings` (the deterministic output plus the score) and
 * `writeup` (Claude's validated output). Pure types only.
 */
import type { GradeLetter, HtmlFacts, PageOutline, Severity } from '@/lib/seo-audit/types'
import type { RunDto, RunRow } from '@/lib/tool-runs/types'

export type OnPageInput = {
  url: string
  /** As typed, trimmed and collapsed. null when the person gave none. */
  keyword: string | null
}

export type OnPageFacts = {
  version: 1
  requestedUrl: string
  finalUrl: string
  finalStatus: number
  redirects: number
  html: HtmlFacts
  outline: PageOutline
  keyword: string | null
  urlPath: string
  collectedAt: string
}

export const ONPAGE_CATEGORIES = [
  'indexability',
  'title',
  'description',
  'headings',
  'keyword',
  'content',
  'images',
  'canonical',
  'social',
] as const

export type OnPageCategory = (typeof ONPAGE_CATEGORIES)[number]

export type OnPageFinding = {
  id: string
  category: OnPageCategory
  severity: Severity
  title: string
  /** The observed value(s), quoted verbatim into the writeup. Never a recommendation. */
  evidence: string
}

export type OnPageScore = {
  score: number
  letter: GradeLetter
  breakdown: Record<OnPageCategory, { deducted: number; cap: number }>
  /** False when no keyword was given, so the keyword category shows as not evaluated. */
  keywordEvaluated: boolean
}

/** The `findings` jsonb column for this tool. */
export type OnPageFindings = {
  findings: OnPageFinding[]
  score: OnPageScore
}

export type PageIntent = 'informational' | 'commercial' | 'transactional' | 'navigational'
export type HeadingRole = 'hero' | 'section' | 'subsection' | 'cta' | 'navigation' | 'noise'
export type HeadingVerdict = 'keep' | 'rewrite' | 'promote' | 'demote' | 'remove'

export type OnPageWriteup = {
  page_intent: { primary: PageIntent; confidence: 'high' | 'medium' | 'low'; rationale: string }
  keyword_fit: { verdict: 'aligned' | 'partial' | 'misaligned' | 'no_keyword'; note: string }
  heading_review: Array<{
    /** Position in facts.outline.headings. */
    index: number
    role: HeadingRole
    verdict: HeadingVerdict
    suggestion: string
  }>
  missing_elements: Array<{ element: string; why: string }>
  rewrites: {
    titles: Array<{ text: string; chars: number }>
    descriptions: Array<{ text: string; chars: number }>
    h1: Array<{ text: string }>
    h2s: Array<{ text: string; replaces_index: number | null }>
  }
  content_gaps: string[]
  summary: string
  caveats: string[]
}

export type OnPageRow = RunRow<OnPageInput, OnPageFacts, OnPageFindings, OnPageWriteup>
export type OnPageDto = RunDto<OnPageInput, OnPageFacts, OnPageFindings, OnPageWriteup>

export const ONPAGE_STAGES = ['fetch', 'write'] as const

/** The keyword field's length cap, shared by the form and parseInput. */
export const KEYWORD_MAX = 80
